import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { Logo, Footer } from "@/components/Chrome";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/spott";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errText = (detail) => {
  if (detail == null) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("La contraseña debe tener al menos 8 caracteres"); return; }
    if (form.password !== form.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: form.password });
      setUser(data);
      toast.success("Contraseña actualizada. ¡Bienvenido de vuelta!");
      navigate(data.role === "admin" || data.role === "agente" ? "/admin" : "/portal");
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-spott-light">
      <div className="relative overflow-hidden bg-spott-blue py-6 grain">
        <div className="mx-auto max-w-7xl px-5 lg:px-10"><Logo reverse /></div>
      </div>
      <div className="mx-auto max-w-md px-5 py-14" data-testid="reset-page">
        <h1 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">Crea una contraseña nueva</h1>
        {token ? (
          <>
            <p className="mt-2 text-sm text-gray-600">
              Elige una contraseña de al menos 8 caracteres. El enlace solo puede usarse una vez.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5 rounded-xl border border-spott-mid bg-white p-6">
              {[["Nueva contraseña", "password", "reset-password-input"],
                ["Repite la contraseña", "confirm", "reset-confirm-input"]].map(([label, key, tid]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-sm font-medium text-spott-blue">{label}</Label>
                  <div className="flex items-center gap-2 rounded-lg border border-spott-mid bg-white px-3">
                    <Lock size={15} className="shrink-0 text-spott-yellow" />
                    <Input type="password" value={form[key]} data-testid={tid} required
                      autoComplete="new-password" className="border-0 px-0 focus-visible:ring-0"
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                  </div>
                </div>
              ))}
              <button type="submit" disabled={busy} data-testid="reset-submit"
                className="w-full rounded-full bg-spott-yellow py-3.5 text-sm font-bold text-spott-blue disabled:opacity-60">
                {busy ? "Guardando…" : "Guardar contraseña"}
              </button>
              <p className="flex gap-2 text-xs text-gray-500">
                <ShieldCheck size={14} className="mt-0.5 shrink-0 text-spott-yellow" />
                Al guardar, iniciaremos tu sesión automáticamente.
              </p>
            </form>
          </>
        ) : (
          <p className="mt-6 rounded-xl border border-spott-mid bg-white p-6 text-sm text-gray-600"
            data-testid="reset-no-token">
            Este enlace no incluye un token válido. Solicita uno nuevo desde{" "}
            <Link to="/login" className="font-semibold text-spott-blue underline">iniciar sesión</Link>.
          </p>
        )}
      </div>
      <Footer />
    </div>
  );
}
